import { MouseEventHandler, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useClickOutside } from '@react-awesome/use-click-outside';

import { useAuth } from 'context/auth';
import { useProfile } from 'context/profile';
import { useEscalationWebSocket } from 'hooks/useEscalationWebSocket';
import { PRODUCT_ACCESSORS } from 'models/Profile';
import { useInfiniteEscalations, useInfinitePriorityNotes } from 'services/api/escalation';
import { useSelectViewActions } from 'stores/selectViewStore';
import { useTaskModalActions, useTaskModalStore } from 'stores/taskModalStore';
import { colors } from 'styles/theme/colors';
import { Label } from 'styles/typography';
import BellSettingsIcon from 'svg/BellSettingsIcon';
import ChevronDownSmall from 'svg/ChevronDownSmall';
import EyeOutlineIcon from 'svg/EyeOutlineIcon';
import Logo from 'svg/Logo';
import LogoutIcon from 'svg/LogoutIcon';
import PriorityFlag from 'svg/PriorityFlag';
import UnsureIcon from 'svg/UnsureIcon';
import WarnIcon from 'svg/WarnIcon';

import TaskModal from './modals/Tasks/TaskModal';
import AccessGuard from './shared/AccessGuard';
import { FooterDot, PrivacyLink } from './shared/MenuFooterComponents';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return (parts[0][0] ?? '').toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UserBar() {
  const { logout } = useAuth();
  const { profile } = useProfile();
  const { setSelectViewOpen } = useSelectViewActions();
  const navigate = useNavigate();

  useEscalationWebSocket();
  const viewedOnLogin = useTaskModalStore((state) => state.viewedOnLogin);
  const { setViewedOnLogin } = useTaskModalActions();

  const [showModal, setShowModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref.current, () => {
    setShowMenu(false);
  });

  const { data: escalationsCount } = useInfiniteEscalations(
    {
      select: (res) => res.pages?.[0].meta.totalRecords,
      enabled: profile.canActAsManager,
    },
    {
      isRespondable: true,
    }
  );

  const { data: priorityNotesCount } = useInfinitePriorityNotes(
    {
      select: (res) => res.pages?.[0].meta.totalRecords,
      enabled: profile.canActAsProvider,
    },
    {
      isRespondable: true,
    }
  );

  const showEscalations =
    profile.canActAsManager && !(profile.canActAsProvider && priorityNotesCount && !escalationsCount);

  useEffect(() => {
    if (
      profile.has(PRODUCT_ACCESSORS.careCoordinationEnabled) &&
      !profile.isAdmin &&
      !viewedOnLogin &&
      (escalationsCount || priorityNotesCount)
    ) {
      setViewedOnLogin();
      setShowModal(true);
    }
  }, [escalationsCount, priorityNotesCount, profile, setViewedOnLogin, viewedOnLogin]);

  const handleLogout: MouseEventHandler = (e) => {
    e.preventDefault();
    setShowMenu(false);
    logout();
  };

  const handlePreferences = () => {
    setShowMenu(false);
    navigate('/preferences');
  };

  const handleSelectView = () => {
    setShowMenu(false);
    setSelectViewOpen(true);
  };

  const initials = getInitials(profile.name);
  const year = new Date().getFullYear();
  const hasSettings = profile.canChangeClientScope || profile.careCoordinationEnabled;

  return (
    <UserBarDiv data-cy='userBarDiv'>
      <LeftGroup>
        <NavLink to='/'>
          <Logo />
        </NavLink>
        {profile.actingClient && <ClientName title={profile.actingClient.name}>{profile.actingClient.name}</ClientName>}
      </LeftGroup>
      <ActionsPalette>
        <AccessGuard accessor={PRODUCT_ACCESSORS.careCoordinationEnabled}>
          <Action
            onClick={() => {
              setShowModal(true);
            }}
            aria-label={showEscalations ? 'Escalations' : 'Priorities'}
            title={showEscalations ? 'Escalations' : 'Priorities'}
            data-cy='tasksLink'>
            <TasksIconContainer $taskCount={showEscalations ? (escalationsCount ?? 0) : (priorityNotesCount ?? 0)}>
              <>
                {showEscalations ? (
                  <>
                    <WarnIcon color='#FFFFFF' width={16} height={16} />
                    {(escalationsCount ?? 0) > 0 && <TasksBadge>{escalationsCount}</TasksBadge>}
                  </>
                ) : (
                  <>
                    <PriorityFlag color='#FFFFFF' width={16} height={16} />
                    {(priorityNotesCount ?? 0) > 0 && <TasksBadge>{priorityNotesCount}</TasksBadge>}
                  </>
                )}
              </>
            </TasksIconContainer>
            <ActionLabel>{showEscalations ? 'Escalations' : 'Priorities'}</ActionLabel>
          </Action>
        </AccessGuard>

        <Action
          onClick={() => {
            window.open('https://support.olio.health/hc/en-us', '_blank');
          }}
          aria-label='Help & Support'
          title='Help & Support'
          data-cy='supportLink'>
          <UnsureIcon color={'#ffffff'} />
          <ActionLabel>Help & Support</ActionLabel>
        </Action>

        <ProfileContainer ref={ref}>
          <ProfilePill onClick={() => setShowMenu((prev) => !prev)} data-cy='userMenu'>
            <PillLabel>{profile.fullName}</PillLabel>
            <ChevronDownSmall color={colors.brandYellow100} />
          </ProfilePill>
          {showMenu && (
            <Menu>
              <UserInfoSection>
                <InitialsAvatar>{initials}</InitialsAvatar>
                <UserInfo>
                  <UserName title={profile.name}>{profile.name}</UserName>
                  <UserEmail title={profile.email}>{profile.email}</UserEmail>
                </UserInfo>
              </UserInfoSection>

              {hasSettings && (
                <SectionGroup>
                  <SectionLabel>Settings</SectionLabel>
                  {profile.canChangeClientScope && (
                    <MenuItem onClick={handleSelectView} data-cy='userMenuSelectClientView'>
                      <MenuIcon>
                        <EyeOutlineIcon width={18} height={18} />
                      </MenuIcon>
                      Select View
                    </MenuItem>
                  )}
                  {profile.careCoordinationEnabled && (
                    <MenuItem onClick={handlePreferences} data-cy='userMenuNotificationPreferences'>
                      <MenuIcon>
                        <BellSettingsIcon width={18} height={18} />
                      </MenuIcon>
                      Notification Preferences
                    </MenuItem>
                  )}
                </SectionGroup>
              )}

              <LogoutMenuItem onClick={handleLogout} data-cy='userMenuLogout'>
                <MenuIcon>
                  <LogoutIcon width={18} height={18} />
                </MenuIcon>
                Logout
              </LogoutMenuItem>

              <MenuFooter>
                <span>&copy; {year} Olio Health</span>
                <FooterDot>&bull;</FooterDot>
                <span>Confidential</span>
                <FooterDot>&bull;</FooterDot>
                <PrivacyLink href='https://policy.olio.health/privacy-policy' target='_blank' rel='noreferrer'>
                  Privacy Policy
                </PrivacyLink>
              </MenuFooter>
            </Menu>
          )}
        </ProfileContainer>
      </ActionsPalette>
      <TaskModal
        profile={profile!}
        setShow={setShowModal}
        show={showModal}
        escalationsCount={escalationsCount ?? 0}
        priorityNotesCount={priorityNotesCount ?? 0}
      />
    </UserBarDiv>
  );
}

const UserBarDiv = styled.div`
  background-color: ${colors.brandNavy100};
  border-bottom: 1px solid ${colors.white25};
  height: ${({ theme }) => theme.dimensions.mainHeaderHeight};
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  min-width: 0;
`;

const ClientName = styled(Label)`
  color: ${colors.white};
  font-weight: 500;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionsPalette = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  color: white;
`;

const Action = styled.div`
  display: flex;
  color: white;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const ActionLabel = styled(Label).attrs({ as: 'span' })`
  color: ${colors.white};
  white-space: nowrap;
`;

const ProfilePill = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 16px;
  border-radius: 999px;
  border: 1px solid ${colors.brandYellow100};
  background: ${colors.brandYellow10};
  cursor: pointer;
`;

const PillLabel = styled(Label)`
  color: ${colors.brandYellow100};
  white-space: nowrap;
`;

const TasksIconContainer = styled.div<{ $taskCount: number }>`
  display: flex;
  gap: 8px;
  padding-right: ${({ $taskCount }) => ($taskCount > 0 ? 7 : 0)}px;
  position: relative;
`;

const TasksBadge = styled.span`
  align-items: center;
  align-self: flex-end;
  background: ${colors.red100};
  border-radius: 50%;
  bottom: -8px;
  color: ${colors.white};
  display: flex;
  font-size: 10px;
  height: 14px;
  justify-content: center;
  margin-left: 1px;
  position: absolute;
  right: 0px;
  width: 14px;
`;

const Menu = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  max-width: 240px;
  overflow: hidden;
  position: absolute;
  right: 24px;
  top: ${({ theme }) => `calc(${theme.dimensions.mainHeaderHeight} + 4px)`};
  z-index: 100;
`;

const UserInfoSection = styled.div`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  display: flex;
  gap: 12px;
  padding: 16px;
`;

const InitialsAvatar = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.blue10};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.blue100};
  display: flex;
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 500;
  height: 44px;
  justify-content: center;
  width: 44px;
`;

const UserInfo = styled.div`
  min-width: 0;
`;

const UserName = styled.div`
  color: ${({ theme }) => theme.colors.black100};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserEmail = styled.div`
  color: ${({ theme }) => theme.colors.black50};
  font-size: 12px;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SectionGroup = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const SectionLabel = styled.div`
  color: ${({ theme }) => theme.colors.black50};
  font-size: 11px;
  padding: 12px 16px 4px;
  text-transform: uppercase;
`;

const MenuItem = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.black75};
  cursor: pointer;
  display: flex;
  gap: 12px;
  padding: 10px 16px;

  &:hover {
    background: ${({ theme }) => theme.colors.blue10};
  }
`;

const MenuIcon = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.black50};
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  width: 20px;
`;

const LogoutMenuItem = styled(MenuItem)`
  color: ${({ theme }) => theme.colors.red100};
  padding: 14px 16px;

  ${MenuIcon} {
    color: ${({ theme }) => theme.colors.red100};
  }
`;

const MenuFooter = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.black3};
  border-top: 1px solid ${({ theme }) => theme.colors.black10};
  color: ${({ theme }) => theme.colors.black50};
  display: flex;
  flex-wrap: wrap;
  font-size: 11px;
  gap: 4px;
  justify-content: center;
  padding: 10px 16px;
`;
